<template>
    <div class="main-container">
        <!-- Header Section -->
        <header class="sticky-header">
            <div class="header-content">
                <img src="@/assets/whitelogo.png" alt="Nexthome4U Logo" class="logo-image" />
                <h1 class="page-title">{{ editData ? 'Edit' : 'Add New' }} {{ objectTypeField }}</h1>
            </div>
        </header>
        
        <!-- Form Section -->
        <main class="form-wrapper">
            <form class="object-form" @submit.prevent="submit" novalidate>
                <!-- Media Uploads Section -->
                <section class="form-section">
                    <h2 class="section-title">
                        <svg class="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                        </svg>
                        Media Uploads
                    </h2>
                    <div :class="objectType == 'HOSTEL' ? 'form-grid upload-images-grid' : 'form-grid'">
                        <!-- Object Image Upload -->
                        <div class="form-field" :class="objectType == 'HOSTEL' ? '' : 'full-width'">
                            <label class="field-label" for="object-image">
                                {{ objectTypeField }} Image <span class="required">*</span>
                            </label>
                            <div v-if="!objectImagePreview" class="upload-area" @click="$refs.objectImageInput.click()" @dragover.prevent @drop.prevent="handleDrop($event, 'objectImage')">
                                <input 
                                    ref="objectImageInput"
                                    type="file" 
                                    id="object-image" 
                                    @change="handleobjectImageUpload" 
                                    accept="image/*"
                                    class="file-input"
                                    aria-describedby="object-image-help"
                                >
                                <div class="upload-content">
                                    <div class="upload-icon-wrapper">
                                        <svg class="upload-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                                        </svg>
                                    </div>
                                    <p class="upload-text">Click to upload or drag & drop</p>
                                    <p class="upload-hint" id="object-image-help">PNG, JPG up to 5MB</p>
                                </div>
                            </div>
                            <div v-if="objectImagePreview" class="image-preview">
                                <div class="preview-container object-preview-container" :class="{ 'uploading': isUploadingObjectImage }">
                                    <img :src="objectImagePreview" alt="Object Image Preview" class="preview-image object-preview-image" />
                                    <div v-if="isUploadingObjectImage" class="upload-loading-overlay">
                                        <div class="spinner-container">
                                            <div class="modern-spinner">
                                                <div class="spinner-ring"></div>
                                                <div class="spinner-ring"></div>
                                                <div class="spinner-ring"></div>
                                            </div>
                                            <p class="uploading-text">Uploading...</p>
                                        </div>
                                    </div>
                                    <button 
                                        v-if="!isUploadingObjectImage"
                                        type="button"
                                        class="remove-button" 
                                        @click="removeobjectImage"
                                        aria-label="Remove image"
                                    >
                                        <svg class="remove-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Room Images Upload (Only for HOSTEL) -->
                        <div v-if="objectType == 'HOSTEL'" class="form-field">
                            <label class="field-label" for="room-images">
                                {{ objectTypeField }} Room Images <span class="required">*</span>
                            </label>
                            <div v-if="roomImagePreviews.length < maxRoomImages" class="upload-area" @click="$refs.roomImagesInput.click()" @dragover.prevent @drop.prevent="handleRoomImagesDrop($event)">
                                <input 
                                    ref="roomImagesInput"
                                    type="file" 
                                    id="room-images" 
                                    @change="handleRoomImagesUpload" 
                                    accept="image/*"
                                    multiple
                                    class="file-input"
                                    aria-describedby="room-images-help"
                                >
                                <div class="upload-content">
                                    <div class="upload-icon-wrapper">
                                        <svg class="upload-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                                        </svg>
                                </div>
                                    <p class="upload-text">{{ roomImagePreviews.length > 0 ? `Change Images (${roomImagePreviews.length}/5)` : 'Click to upload or drag & drop' }}</p>
                                    <p class="upload-hint" id="room-images-help">Select up to 5 images (PNG, JPG up to 5MB each)</p>
                            </div>
                                            </div>
                            <p v-if="roomImagePreviews.length < maxRoomImages" class="field-help">You can select up to 5 room images at once</p>
                            <p v-else class="field-help">Maximum of 5 room images selected. Remove an image to add a new one.</p>
                            <div v-if="roomImagePreviews.length > 0" class="room-images-preview-grid">
                                <div v-for="(preview, index) in roomImagePreviews" :key="index" class="image-preview-item">
                                    <div class="preview-container" :class="{ 'uploading': isUploadingRoomImages[index] }">
                                        <img :src="preview.url" :alt="`Room Image ${index + 1} Preview`" class="preview-image" />
                                        <div v-if="isUploadingRoomImages[index]" class="upload-loading-overlay">
                                        <div class="spinner-container">
                                            <div class="modern-spinner">
                                                <div class="spinner-ring"></div>
                                                <div class="spinner-ring"></div>
                                                <div class="spinner-ring"></div>
                                            </div>
                                            <p class="uploading-text">Uploading...</p>
                                        </div>
                                    </div>
                                    <button 
                                            v-if="!isUploadingRoomImages[index]"
                                        type="button"
                                        class="remove-button" 
                                            @click="removeRoomImage(index)"
                                            :aria-label="`Remove room image ${index + 1}`"
                                    >
                                            <svg class="remove-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                            </svg>
                                    </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Basic Details Section -->
                <section class="form-section">
                    <h2 class="section-title">
                        <svg class="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        Basic Details
                    </h2>
                    <div class="form-grid">
                        <div class="form-field">
                            <label class="field-label" for="object-name">
                                {{ objectTypeField }} Name <span class="required">*</span>
                            </label>
                            <input 
                                id="object-name"
                                class="form-input" 
                                type="text" 
                                v-model="objectName"
                                :placeholder="`Enter ${objectPlaceholder} Name`" 
                                required
                                aria-describedby="object-name-help"
                            >
                            <p class="field-help" id="object-name-help">Enter the name of your {{ objectTypeField.toLowerCase() }}</p>
                        </div>
                        
                        <div class="form-field">
                            <label class="field-label" for="branch-name">
                                Branch Name <span class="required">*</span>
                            </label>
                            <input 
                                id="branch-name"
                                class="form-input" 
                                type="text" 
                                v-model="branchName"
                                placeholder="Enter Branch Name" 
                                required
                            >
                        </div>
                        
                        <div class="form-field">
                            <label class="field-label" for="object-type">
                                {{ objectTypeField }} Type <span class="required">*</span>
                            </label>
                            <select 
                                id="object-type"
                                class="form-select" 
                                v-model="hostelType" 
                                required
                            >
                                <option value="">Select {{ objectTypeField }} Type</option>
                                <option v-for="type in objectCategory" :key="type" :value="type">{{ type }}</option>
                            </select>
                        </div>
                        
                        <div class="form-field">
                            <label class="field-label" for="total-rooms">
                                Total Rooms <span class="required">*</span>
                            </label>
                            <input 
                                id="total-rooms"
                                class="form-input" 
                                type="number" 
                                v-model="totalRoom"
                                placeholder="Enter Number of Rooms" 
                                required
                                min="1"
                            >
                        </div>
                            <div class="form-field">
                            <label class="field-label" for="onboarding-days">
                                Onboarding Days <span class="required">*</span>
                            </label>
                            <input 
                                id="onboarding-days"
                                class="form-input" 
                                type="number" 
                                v-model="onboardingDays"
                                placeholder="Enter Onboarding Days" 
                                required
                                min="1"
                            >
                        </div>
                    </div>
                </section>

                <!-- Contact Details Section -->
                <section class="form-section">
                    <h2 class="section-title">
                        <svg class="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                        </svg>
                        Contact Details
                    </h2>
                    <div class="form-grid">
                        <div class="form-field">
                            <label class="field-label" for="city">
                                City <span class="required">*</span>
                            </label>
                            <input 
                                id="city"
                                class="form-input" 
                                type="text" 
                                v-model="city"
                                placeholder="Enter City Name" 
                                required
                            >
                        </div>
                        
                        <div class="form-field">
                            <label class="field-label" for="pan">
                                PAN <span class="required">*</span>
                            </label>
                            <input 
                                id="pan"
                                class="form-input" 
                                type="text" 
                                v-model="pan" 
                                placeholder="Enter PAN Number"
                                required
                                pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                                title="Enter a valid PAN number (e.g., ABCDE1234F)"
                            >
                        </div>
                        
                        <div class="form-field">
                            <label class="field-label" for="mobile">
                                Mobile Number <span class="required">*</span>
                            </label>
                            <input 
                                id="mobile"
                                class="form-input" 
                                type="tel" 
                                v-model="mobileNumber"
                                placeholder="Enter Mobile Number" 
                                required
                                pattern="[0-9]{10}"
                                title="Enter a valid 10-digit mobile number"
                            >
                        </div>
                        
                        <div class="form-field">
                            <label class="field-label" for="alt-mobile">
                                Alternate Mobile Number
                            </label>
                            <input 
                                id="alt-mobile"
                                class="form-input" 
                                type="tel" 
                                v-model="alternateMobileNumber"
                                placeholder="Enter Alternate Mobile Number"
                                pattern="[0-9]{10}"
                                title="Enter a valid 10-digit mobile number"
                            >
                        </div>
                        
                        <div class="form-field">
                            <label class="field-label" for="warden-number">
                                Warden Number <span class="required">*</span>
                            </label>
                            <input 
                                id="warden-number"
                                class="form-input" 
                                type="text" 
                                v-model="wardenNumber"
                                placeholder="Enter Warden Number" 
                                required
                            >
                        </div>
                    </div>
                </section>
                <!-- Location Details Section -->
                <section class="form-section">
                    <h2 class="section-title">
                        <svg class="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        </svg>
                        Location Details
                    </h2>
                    <div class="form-grid">
                        <div class="form-field">
                            <label class="field-label" for="landmark">
                                Landmark Place <span class="required">*</span>
                            </label>
                            <input 
                                id="landmark"
                                class="form-input" 
                                type="text" 
                                v-model="place"
                                placeholder="e.g., from Kathmandu Mall" 
                                required
                            >
                        </div>
                        <div class="form-field">
                            <label class="field-label" for="distance">
                                Landmark Distance <span class="required">*</span>
                            </label>
                            <input 
                                id="distance"
                                class="form-input" 
                                type="number" 
                                v-model="distance"
                                placeholder="Enter Distance (e.g., 1, 500)" 
                                required
                                min="0"
                                step="0.1"
                            >
                        </div>
                        
                        <div class="form-field">
                            <label class="field-label" for="unit">
                                Distance Unit <span class="required">*</span>
                            </label>
                            <select 
                                id="unit"
                                class="form-select" 
                                v-model="unit" 
                                required
                            >
                                <option value="">Select Unit</option>
                                <option value="M">Meters (M)</option>
                                <option value="KM">Kilometers (KM)</option>
                            </select>
                        </div>
                        
                        
                        <div class="form-field">
                            <label class="field-label" for="map-url">
                                Google Maps URL <span class="required">*</span>
                            </label>
                            <input 
                                id="map-url"
                                class="form-input" 
                                type="url" 
                                v-model="mapUrl"
                                placeholder="https://maps.google.com/..." 
                                required
                                pattern="https?://.+"
                                title="Enter a valid URL starting with http:// or https://"
                            >
                        </div>
                        
                        <div class="form-field full-width">
                            <label class="field-label" for="address">
                                Complete Address <span class="required">*</span>
                            </label>
                            <textarea 
                                id="address"
                                class="form-textarea" 
                                v-model="address" 
                                placeholder="Enter complete address with street, area, city, postal code"
                                required
                                rows="3"
                            ></textarea>
                        </div>
                    </div>
                </section>

                <!-- Room & Price Info Section -->
                <section class="form-section">
                    <h2 class="section-title">
                        <svg class="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        Room & Price Information
                    </h2>
                    <div class="form-grid">
                        <div class="form-field">
                            <label class="field-label" for="admission-fee">
                                Admission Fee Type <span class="required">*</span>
                            </label>
                            <select 
                                id="admission-fee"
                                class="form-select" 
                                v-model="admissionFeeType" 
                                required
                            >
                                <option value="">Select Fee Type</option>
                                <option v-for="type in admissionFeeTypes" :key="type" :value="type">
                                    {{ type === 'FREE' ? 'Free Admission' : 'Paid Admission' }}
                                </option>
                            </select>
                        </div>
                        
                        <div class="form-field full-width">
                            <div class="room-pricing-section">
                                <h3 class="subsection-title">
                                    Room Availability & Pricing <span class="required">*</span>
                                </h3>
                                <div class="room-pricing-list">
                                    <div v-for="(pair, index) in roomAvailabilityPairs" :key="index" class="room-pricing-item">
                                        <div class="pricing-inputs">
                                            <div class="input-group">
                                                <label class="input-label">Room Type</label>
                                                <select 
                                                    v-model="pair.type" 
                                                    class="form-select compact" 
                                                    :disabled="isSubmitting"
                                                    required
                                                >
                                                    <option disabled value="">Select Room Type</option>
                                                    <option v-for="type in roomTypes" :key="type" :value="type"
                                                        :disabled="getDisabledTypes(index).includes(type)">
                                                        {{ type }} Sharing
                                                    </option>
                                                </select>
                                            </div>
                                            <div class="input-group">
                                                <label class="input-label">Monthly Price</label>
                                                <input 
                                                    type="number" 
                                                    v-model.number="pair.price" 
                                                    placeholder="Enter monthly price"
                                                    class="form-input compact" 
                                                    :disabled="isSubmitting"
                                                    required
                                                    min="0"
                                                />
                                            </div>
                                            <button 
                                                v-if="roomAvailabilityPairs.length > 1" 
                                                type="button" 
                                                class="btn-remove"
                                                @click="removeRoomPair(index)"
                                                :disabled="isSubmitting"
                                                aria-label="Remove room type"
                                            >
                                                <svg class="btn-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <button 
                                    @click="addRoomPair" 
                                    type="button" 
                                    class="btn-add"
                                    :disabled="roomAvailabilityPairs.length >= roomTypes.length || isSubmitting"
                                >
                                    <svg class="btn-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                                    </svg>
                                    Add Room Type
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
                <!-- Additional Information Section -->
                <section class="form-section additional-info-section">
                    <h2 class="section-title">
                        <svg class="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        Additional Information
                    </h2>
                    <div class="form-grid additional-info-grid">
                        <div class="form-field tag-input-field">
                            <TagInput 
                                v-model="facilitys" 
                                label="Facilities" 
                                placeholder="Type facility and press Enter"
                                :required="true" 
                            />
                            <p class="field-help tag-help">Add facilities like WiFi, AC, Parking, etc.</p>
                        </div>
                        
                        <div class="form-field tag-input-field">
                            <TagInput 
                                v-model="tags" 
                                label="Tags" 
                                placeholder="Type tag and press Enter"
                                :required="true" 
                            />
                            <p class="field-help tag-help">Add relevant tags for better searchability</p>
                        </div>
                        
                        <div class="form-field full-width description-field">
                            <label class="field-label" for="description">
                                {{ objectTypeField }} Description <span class="required">*</span>
                            </label>
                            <textarea 
                                id="description"
                                class="form-textarea" 
                                v-model="objectDescription"
                                :placeholder="`Provide a detailed description of your ${objectPlaceholder.toLowerCase()}`" 
                                required
                                rows="4"
                            ></textarea>
                            <p class="field-help">Describe the key features, amenities, and unique selling points</p>
                        </div>
                    </div>
                </section>

                <!-- Check-in and Check-out Section -->
                <section v-if="objectType === 'HOTEL'" class="form-section">
                    <h2 class="section-title">
                        <svg class="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        Check-in & Check-out Times
                    </h2>
                    <div class="form-grid">
                        <div class="form-field">
                            <label class="field-label" for="checkin-time">
                                Check-in Time <span class="required">*</span>
                            </label>
                            <CustomTimePicker v-model="checkInTime" id="checkin-time" />
                            <p class="field-help">Standard check-in time for guests</p>
                        </div>
                        <div class="form-field">
                            <label class="field-label" for="checkout-time">
                                Check-out Time <span class="required">*</span>
                            </label>
                            <CustomTimePicker v-model="checkOutTime" id="checkout-time" />
                            <p class="field-help">Standard check-out time for guests</p>
                        </div>
                    </div>
                </section>
            </form>
            
            <!-- Form Actions -->
            <div class="form-actions">
                <button type="button" class="btn btn-secondary" @click="cancel">
                    <svg class="btn-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                    Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="isSubmitting" @click="submit">
                    <svg v-if="!isSubmitting" class="btn-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path v-if="editData" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                        <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                    </svg>
                    <span v-if="isSubmitting" class="spinner"></span>
                    {{ isSubmitting ? 'Processing...' : (editData ? 'Update' : 'Create') }} {{ objectTypeField }}
                </button>
            </div>
        </main>
        
        <!-- Toast Notifications -->
        <div class="toast-container">
            <transition-group name="toast" tag="div">
                <div 
                    v-for="toast in toasts" 
                    :key="toast.id" 
                    class="toast"
                    :class="[`toast-${toast.type}`]"
                >
                    <div class="toast-icon">
                        <svg v-if="toast.type === 'error'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="15" y1="9" x2="9" y2="15"/>
                            <line x1="9" y1="9" x2="15" y2="15"/>
                        </svg>
                        <svg v-else-if="toast.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22,4 12,14.01 9,11.01"/>
                        </svg>
                        <svg v-else-if="toast.type === 'info'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="16" x2="12" y2="12"/>
                            <line x1="12" y1="8" x2="12.01" y2="8"/>
                        </svg>
                        <svg v-else-if="toast.type === 'warning'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
                            <line x1="12" y1="9" x2="12" y2="13"/>
                            <line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                    </div>
                    <div class="toast-content">
                        <p class="toast-message">{{ toast.message }}</p>
                    </div>
                    <button class="toast-close" @click="removeToast(toast.id)">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>
            </transition-group>
        </div>
    </div>
</template>

<script>
import apiService from '@/services/api';
import TagInput from './TagInput.vue';
import CustomTimePicker from './CustomTimePicker.vue';
import { isNoDataError, isHmsSubscriptionInactiveError } from '@/utilities/errorHelper';

export default {
    name: 'AddNewObject',
    components: {
        TagInput,
        CustomTimePicker,
    },
    props: {
        editData: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            isSubmitting: false,
            objectName: '',
            branchName: '',
            hostelType: '',
            objectType: '',
            totalRoom: '',
            checkInTime: '',
            checkOutTime: '',
            city: '',
            pan: '',
            address: '',
            roomAvailabilityPairs: [
                { type: '', price: null }
            ],
            roomAvailabilityType: [],
            monthlyFee: '',
            admissionFeeType: '',
            facility: '',
            facilitys: [],
            objectDescription: '',
            tag: '',
            tags: [],
            objectImage: null,
            objectRoomImages: [],
            roomImagePreviews: [], // Array of { url: string, filename: string }
            objectImagePreview: null,
            objectImageUrl: null,
            objectCategory: [],
            toasts: [],
            maxFileSize: 5 * 1024 * 1024, // 5MB
            allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
            isUploadingObjectImage: false,
            isUploadingRoomImages: {}, // Object tracking upload state by index
            maxRoomImages: 5,
            roomTypes: ['1S', '2S', '3S', '4S', '5S', '6S'],
            admissionFeeTypes: ['FREE', 'PAID'],
            objectTypeField: '',
            roomtypeprice: {},
            mobileNumber: '',
            alternateMobileNumber: '',
            wardenNumber: '',
            distance: '',
            unit: '',
            place: '',
            mapUrl: '',
            onboardingDays:''
        };
    },
    mounted() {
        let adminProfileData = localStorage.getItem('ADMIN_PROFILE_DATA');
        adminProfileData = JSON.parse(adminProfileData);
        let objectTypeField = adminProfileData.adminType;
        this.objectTypeField = objectTypeField.charAt(0).toUpperCase() + objectTypeField.slice(1).toLowerCase();
        this.objectType = adminProfileData.adminType;
        if (adminProfileData.adminType == 'HOTEL') { this.objectCategory = ['STANDORD', 'DORMITORY', 'ALL']; }
        if (adminProfileData.adminType == 'HOUSE') { this.objectCategory = ['FAMILY', 'MALE', 'FEMALE', 'ALL']; }
        if (adminProfileData.adminType == 'HOSTEL') { this.objectCategory = ['BOYS', 'GIRLS']; }
        if (adminProfileData.adminType == 'PROPERTY') { this.objectCategory = ['HOUSE', 'LAND']; }

        this.populateFormFromEditData(this.editData);
    },
    computed: {
        objectPlaceholder() {
            return this.objectTypeField;
        },
    },
    watch: {
        editData: {
            handler(newValue) {
                this.populateFormFromEditData(newValue);
            },
            immediate: true
        },
        roomAvailabilityPairs: {
            handler(newValue) {
                // Update roomAvailabilityType
                this.roomAvailabilityType = newValue.map(pair => pair.type).filter(type => type);

                // Calculate the minimum price and update monthlyFee
                const prices = newValue.map(pair => pair.price).filter(price => price !== null && price !== undefined);
                this.monthlyFee = prices.length > 0 ? String(Math.min(...prices)) : '';
            },
            deep: true // Ensures the watcher reacts to changes within the array
        }
    },
    methods: {
        populateFormFromEditData(editData) {
            if (!editData) return;

            let nearByData = editData.nearBy;
            if (typeof nearByData === 'string') {
                try {
                    nearByData = JSON.parse(nearByData);
                } catch (e) {
                    nearByData = {};
                }
            }
            if (!nearByData || typeof nearByData !== 'object') {
                nearByData = {};
            }

            this.objectName = editData.objectName || '';
            this.branchName = editData.branch || '';
            this.hostelType = editData.hostelType || '';
            this.objectType = editData.objectType || '';
            this.totalRoom = editData.totalRoom || '';
            this.city = editData.city || '';
            this.pan = editData.pan || '';
            this.address = editData.address || '';
            this.admissionFeeType = editData.admissionFee || '';
            this.facilitys = editData.facility || [];
            this.objectDescription = editData.objectDescription || '';
            this.tags = editData.tag || [];
            this.objectImageUrl = editData.objectImage || '';
            this.objectImagePreview = editData.objectImage || '';
            this.objectRoomImages = editData.hostelRoomImage || [];

            // Initialize room image previews from existing images
            this.roomImagePreviews = this.objectRoomImages.map(url => ({
                url: url,
                filename: url
            }));

            this.roomAvailabilityPairs = [];
            if (editData.roomtypeprice) {
                for (const [type, price] of Object.entries(editData.roomtypeprice)) {
                    this.roomAvailabilityPairs.push({ type, price });
                }
            }
            if (this.roomAvailabilityPairs.length === 0) {
                this.roomAvailabilityPairs = [{ type: '', price: null }];
            }

            this.mobileNumber = editData.contactDetails?.mobileNumber || '';
            this.alternateMobileNumber = editData.contactDetails?.alternateMobileNumber || '';
            this.wardenNumber = editData.wardenNumber || '';
            this.distance = nearByData.distance || editData.distance || '';
            this.unit = nearByData.unit || editData.unit || '';
            this.place = nearByData.place || editData.place || '';
            this.mapUrl = editData.mapUrl || '';
            this.checkInTime = editData.checkInTime || '';
            this.checkOutTime = editData.checkOutTime || '';
            this.onboardingDays = editData.onBoardingDays || '';
        },
        /** Extract filename from URL or return as-is if already a filename */
        extractFilename(url) {
            if (!url) return '';
            // If it's already just a filename (no slashes or http), return as-is
            if (!url.includes('/') && !url.includes('http')) {
                return url;
            }
            // Extract filename from URL
            try {
                const urlObj = new URL(url);
                const pathname = urlObj.pathname;
                return pathname.split('/').pop() || url;
            } catch (e) {
                // If URL parsing fails, try to extract from path
                const parts = url.split('/');
                return parts[parts.length - 1] || url;
            }
        },
        addRoomPair() {
            this.roomAvailabilityPairs.push({ type: '', price: null });
        },
        removeRoomPair(index) {
            if (this.roomAvailabilityPairs.length > 1) {
                this.roomAvailabilityPairs.splice(index, 1);
            } else {
                this.showToast('At least one room availability type must remain.', 'warning');
            }
        },
        getDisabledTypes(index) {
            return this.roomAvailabilityPairs
                .filter((pair, i) => i !== index && pair.type)
                .map(pair => pair.type);
        },
        
        /** Handle drag and drop for file uploads */
        handleDrop(event, imageType) {
            const files = event.dataTransfer.files;
            if (files.length > 0) {
                const file = files[0];
                if (file.type.startsWith('image/')) {
                    const fakeEvent = { target: { files: [file] } };
                    if (imageType === 'objectImage') {
                            this.handleobjectImageUpload(fakeEvent);
                    }
                } else {
                    this.showToast('Please drop an image file.', 'error');
                }
            }
        },
        /** Handle image selections with preview generation */
        async handleobjectImageUpload(event) {
            const file = event.target.files[0];
            if (!file) return;
            
            // Check file size (5MB limit)
            if (file.size > this.maxFileSize) {
                this.showToast('Image size exceeds 5MB limit. Please select a smaller image.', 'error');
                event.target.value = '';
                return;
            }
            
            if (file.type.startsWith('image/')) {
                // Create preview immediately
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.objectImagePreview = e.target.result;
                };
                reader.readAsDataURL(file);
                
                // Upload image immediately
                this.isUploadingObjectImage = true;
                await this.uploadSingleImage(file, 'objectImage');
                this.isUploadingObjectImage = false;
            } else {
                this.showToast('Invalid file type. Please upload a valid image file (JPEG, PNG, GIF, or WebP) under 5MB.', 'error');
                event.target.value = '';
            }
        },
        async handleRoomImagesUpload(event) {
            const files = Array.from(event.target.files || []);
            if (files.length === 0) return;
            
            // Check total number of images
            const currentCount = this.roomImagePreviews.length;
            const remainingSlots = this.maxRoomImages - currentCount;
            
            if (files.length > remainingSlots) {
                this.showToast(`You can only upload up to ${this.maxRoomImages} images. Please select ${remainingSlots} or fewer images.`, 'error');
                event.target.value = '';
                return;
            }
            
            // Validate all files before processing
            const invalidFiles = files.filter(file => {
                return !file.type.startsWith('image/') || file.size > this.maxFileSize;
            });
            
            if (invalidFiles.length > 0) {
                this.showToast('Some files are invalid. Please ensure all files are images under 5MB.', 'error');
                event.target.value = '';
                return;
            }
            
            // Process each file
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const previewIndex = this.roomImagePreviews.length;
                
                // Create preview first
                const previewUrl = await new Promise((resolve) => {
                const reader = new FileReader();
                    reader.onload = (e) => resolve(e.target.result);
                reader.readAsDataURL(file);
                });
                
                // Add preview to array
                this.roomImagePreviews.push({ url: previewUrl, filename: null });
                
                // Upload image immediately
                this.isUploadingRoomImages[previewIndex] = true;
                await this.uploadRoomImage(file, previewIndex);
                this.isUploadingRoomImages[previewIndex] = false;
            }
            
            // Reset input
                event.target.value = '';
        },
        handleRoomImagesDrop(event) {
            const files = Array.from(event.dataTransfer.files || []).filter(file => file.type.startsWith('image/'));
            if (files.length > 0) {
                // Create a fake event object to reuse the upload handler
                const fakeEvent = { target: { files: files } };
                this.handleRoomImagesUpload(fakeEvent);
            } else {
                this.showToast('Please drop image files.', 'error');
            }
        },

        /** Upload a single image immediately */
        async uploadSingleImage(file, type) {
            try {
                const formData = new FormData();
                formData.append('files', file);

                const uploadResponse = await apiService.uploadImage(formData);

                const uploadedFilenames = uploadResponse.data || [];
                if (uploadedFilenames.length > 0) {
                    // Store just the filename
                    const imageFilename = uploadedFilenames[0];
                    
                    // Set the appropriate URL field based on type
                    if (type === 'objectImage') {
                        this.objectImageUrl = imageFilename;
                    }
                    
                    this.showToast('Image uploaded successfully!', 'success', 3000);
                } else {
                    throw new Error('No image URL received from server');
                }
            } catch (error) {
                console.error('Image upload failed:', error);
                // Skip error handling for "no data" cases (status 400 with errorcode 2003)
                if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
                    return;
                }
                
                let message = null;
                if (error?.response?.data) {
                    const errorData = error.response.data;
                    const status = error.response.status;
                    
                    // For HTTP 400 errors, always use the reason field from API response
                    if (status === 400) {
                        if (errorData.reason) {
                            message = errorData.reason;
                        }
                        // If no reason provided for 400 error, don't show toast (no generic messages)
                    } else {
                        // For other status codes, prioritize reason field
                        if (errorData.reason) {
                            message = errorData.reason;
                        } else if (errorData.message) {
                            message = errorData.message;
                        }
                    }
                }
                
                // Only show toast if we have a message (genuine errors only)
                if (message) {
                    this.showToast(message, 'error');
                }
                
                // Remove preview if upload failed
                if (type === 'objectImage') {
                    this.objectImagePreview = null;
                    this.isUploadingObjectImage = false;
                }
            }
        },
        /** Upload a room image */
        async uploadRoomImage(file, previewIndex) {
            try {
                const formData = new FormData();
                formData.append('files', file);

                const uploadResponse = await apiService.uploadImage(formData);

                const uploadedFilenames = uploadResponse.data || [];
                if (uploadedFilenames.length > 0) {
                    // Store just the filename
                    const imageFilename = uploadedFilenames[0];
                    
                    // Update preview with filename
                    if (this.roomImagePreviews[previewIndex]) {
                        this.roomImagePreviews[previewIndex].filename = imageFilename;
                    }
                    
                    // Add to objectRoomImages array if not already present
                    if (!this.objectRoomImages.includes(imageFilename)) {
                        this.objectRoomImages.push(imageFilename);
                    }
                } else {
                    throw new Error('No image URL received from server');
                }
            } catch (error) {
                console.error('Room image upload failed:', error);
                // Skip error handling for "no data" cases (status 400 with errorcode 2003)
                if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
                    return;
                }
                
                let message = null;
                if (error?.response?.data) {
                    const errorData = error.response.data;
                    const status = error.response.status;
                    
                    // For HTTP 400 errors, always use the reason field from API response
                    if (status === 400) {
                        if (errorData.reason) {
                            message = errorData.reason;
                        }
                        // If no reason provided for 400 error, don't show toast (no generic messages)
                    } else {
                        // For other status codes, prioritize reason field
                        if (errorData.reason) {
                            message = errorData.reason;
                        } else if (errorData.message) {
                            message = errorData.message;
                        }
                    }
                }
                
                // Only show toast if we have a message (genuine errors only)
                if (message) {
                    this.showToast(message, 'error');
                }
                
                // Remove preview if upload failed
                if (this.roomImagePreviews[previewIndex]) {
                    this.roomImagePreviews.splice(previewIndex, 1);
                    delete this.isUploadingRoomImages[previewIndex];
                }
            }
        },

        // Toast notification methods
        showToast(message, type = 'error', duration = 5000) {
            const id = Date.now() + Math.random();
            this.toasts.push({ id, message, type });
            
            if (duration > 0) {
                setTimeout(() => {
                    this.removeToast(id);
                }, duration);
            }
        },
        removeToast(id) {
            this.toasts = this.toasts.filter(toast => toast.id !== id);
        },
        
        /** Remove image and preview */
        removeobjectImage() {
            this.objectImage = null;
            this.objectImagePreview = null;
            this.objectImageUrl = null;
            // Reset file input if it exists in the DOM
            const inputElement = document.getElementById('object-image');
            if (inputElement) {
                inputElement.value = '';
            }
        },
        removeRoomImage(index) {
            if (this.roomImagePreviews[index]) {
                const preview = this.roomImagePreviews[index];
                const filenameToRemove = preview.filename || preview.url;
                
            // Remove from objectRoomImages if present
                if (filenameToRemove) {
                    const urlIndex = this.objectRoomImages.indexOf(filenameToRemove);
                    if (urlIndex > -1) {
                        this.objectRoomImages.splice(urlIndex, 1);
                    }
                }
                
                // Remove from previews array
                this.roomImagePreviews.splice(index, 1);
                
                // Remove from uploading states and re-index
                const newUploadingStates = {};
                Object.keys(this.isUploadingRoomImages).forEach(key => {
                    const keyIndex = parseInt(key);
                    if (keyIndex < index) {
                        newUploadingStates[keyIndex] = this.isUploadingRoomImages[keyIndex];
                    } else if (keyIndex > index) {
                        newUploadingStates[keyIndex - 1] = this.isUploadingRoomImages[keyIndex];
                    }
                });
                this.isUploadingRoomImages = newUploadingStates;
            }
        },

        /** Reset the form */
        cancel() {
            this.$emit('close');
        },

        /** Submit the form */
async submit() {
    this.isSubmitting = true;
    try {
        // Validate required images (images should already be uploaded)
        if (!this.objectImageUrl) {
            this.showToast('Please upload the object image.', 'error');
            this.isSubmitting = false;
            return;
        }
        
        // Validate room images (at least one required for HOSTEL type)
        if (this.objectType === 'HOSTEL' && this.objectRoomImages.length === 0) {
            this.showToast('Please upload at least one room image.', 'error');
            this.isSubmitting = false;
            return;
        }

        // Prepare roomAvailabilityType and roomtypeprice from roomAvailabilityPairs
        this.roomAvailabilityType = this.roomAvailabilityPairs.map(pair => pair.type).filter(type => type);
        this.roomtypeprice = {};
        this.roomAvailabilityPairs.forEach(pair => {
            if (pair.type && pair.price !== null) {
                this.roomtypeprice[pair.type] = pair.price;
            }
        });

        // Extract filenames from URLs for images
        const objectImageFilename = this.extractFilename(this.objectImageUrl);
        const hostelRoomImageFilenames = this.objectRoomImages.map(url => this.extractFilename(url));

        // Prepare object data
        const objectData = {
            objectName: this.objectName,
            objectType: this.objectType,
            address: this.address,
            tag: this.tags,
            city: this.city,
            objectImage: objectImageFilename,
            objectDescription: this.objectDescription,
            availabilityid: "FzxO3hgdihWc",
            hostelType: this.hostelType,
            hostelRoomImage: hostelRoomImageFilenames,
            admissionFee: this.admissionFeeType,
            roomtypeprice: this.roomtypeprice,
            branch: this.branchName,
            totalRoom: String(this.totalRoom || ''),
            availableRoomType: this.roomAvailabilityType,
            mobileNumber: this.mobileNumber,
            alternateMobileNumber: this.alternateMobileNumber,
            wardenNumber: this.wardenNumber,
            facility: this.facilitys,
            pan: this.pan,
            monthlyFee: this.monthlyFee,
            distance: String(this.distance || ''),
            unit: this.unit,
            place: this.place,
            mapUrl: this.mapUrl,
            checkInTime: this.checkInTime,
            checkOutTime: this.checkOutTime,
            onBoardingDays: String(this.onboardingDays || '')
        };

        if (this.editData && this.editData.objectId) {
            // UPDATE mode
            const response = await apiService.updateObject(this.editData.objectId, objectData);
            console.log('object updated successfully:', response.data);
        } else {
            // ADD mode
            const response = await apiService.createObject(objectData);
            console.log('object created successfully:', response.data);
        }

        localStorage.removeItem("ADMIN_OBJECT_LIST");
        this.$emit('close');
        window.location.reload();
    } catch (error) {
        this.isSubmitting = false;
        console.error('Error during submission:', error);
        // Skip error handling for "no data" cases (status 400 with errorcode 2003)
        if (isNoDataError(error) || isHmsSubscriptionInactiveError(error)) {
            return;
        }
        
        let message = null;
        if (error?.response?.data) {
            const errorData = error.response.data;
            const status = error.response.status;
            
            // For HTTP 400 errors, always use the reason field from API response
            if (status === 400) {
                if (errorData.reason) {
                    message = errorData.reason;
                }
                // If no reason provided for 400 error, don't show toast (no generic messages)
            } else {
                // For other status codes, prioritize reason field
                if (errorData.reason) {
                    message = errorData.reason;
                } else if (errorData.message) {
                    message = errorData.message;
                } else {
                    message = 'Failed to submit object. Please try again.';
                }
            }
        } else {
            message = 'Failed to submit object. Please try again.';
        }
        
        // Only show toast if we have a message (genuine errors only)
        if (message) {
            this.showToast(message, 'error');
        }
    }
},
    },
};
</script>

<style scoped>
/* Component-specific CSS variables that work with scoped styles */

/* Base Styles */
* {
    box-sizing: border-box;
}

.main-container {
    min-height: 100%;
    width: 100%;
    background: linear-gradient(135deg, #f7fafc 0%, #e3f2fd 100%);
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: #2d3748;
    line-height: 1.6;
    display: flex;
    flex-direction: column;
    position: relative;
}

/* Header Styles */
.sticky-header {
    position: -webkit-sticky; /* Safari support */
    position: sticky;
    top: 0;
    background: linear-gradient(135deg, #5b2e8e 0%, #4a2373 100%);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    z-index: 150;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    width: 100%;
    flex-shrink: 0;
    margin: 0;
    align-self: flex-start;
}

.header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem 2rem;
    width: 100%;
    position: relative;
    z-index: 1;
}

.logo-image {
    height: 40px;
    width: auto;
    filter: brightness(1.1);
}

.page-title {
    color: #ffffff;
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* Form Container */
.form-wrapper {
    width: 100%;
    padding: 2rem;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    padding-bottom: 0;
    position: relative;
    box-sizing: border-box;
}

.object-form {
    background: #ffffff;
    border-radius: 0.75rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
    padding: 3rem;
    margin: 0 auto;
    max-width: 1400px;
    width: 100%;
    margin-bottom: 0;
    padding-bottom: 3rem;
    box-sizing: border-box;
}

/* Section Styles */
.form-section {
    margin-bottom: 3rem;
    padding-bottom: 2.5rem;
    border-bottom: 1px solid #e2e8f0;
}

.form-section:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
}

.section-title {
    display: flex;
    align-items: center;
    gap: 0.875rem;
    font-size: 1.5rem;
    font-weight: 700;
    color: #1a202c;
    margin: 0 0 2rem 0;
    padding-bottom: 0.875rem;
    border-bottom: 2px solid #5b2e8e;
    position: relative;
}

.subsection-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0 0 1.25rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-bottom: 0.625rem;
    border-bottom: 1px solid #e2e8f0;
}

.section-icon {
    width: 24px;
    height: 24px;
    color: #5b2e8e;
    flex-shrink: 0;
    stroke-width: 2;
    transition: all 0.2s ease;
}

.section-title:hover .section-icon {
    color: #7c4dbd;
    transform: scale(1.1);
}

/* Form Grid */
.form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 1.75rem;
    align-items: start;
    width: 100%;
}

.upload-images-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
}

.room-images-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}

.form-field {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    position: relative;
}

.form-field.full-width {
    grid-column: 1 / -1;
}

/* Form Controls */
.field-label {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.375rem;
    display: block;
    text-align: left;
    letter-spacing: 0.01em;
    transition: color 0.2s ease;
}

.form-field:focus-within .field-label {
    color: #5b2e8e;
}

.required {
    color: #f56565;
    font-weight: 700;
    margin-left: 2px;
}

.field-help {
    font-size: 0.8125rem;
    color: #6b7280;
    margin: 0.375rem 0 0 0;
    line-height: 1.5;
    font-weight: 400;
}

/* Additional Information Section Specific Styles */
.additional-info-section {
    position: relative;
}

.additional-info-grid {
    gap: 2rem;
}

.tag-input-field {
    margin-bottom: 0.5rem;
}

.tag-input-field .tag-input-wrapper {
    margin-bottom: 0.25rem;
}

.tag-help {
    margin-top: 0.5rem;
    font-size: 0.8125rem;
    color: #6b7280;
    line-height: 1.4;
}

.description-field {
    margin-top: 0.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e5e7eb;
}

.description-field .field-label {
    margin-bottom: 0.75rem;
}

.description-field .form-textarea {
    min-height: 120px;
    line-height: 1.6;
}

.form-input,
.form-select,
.form-textarea {
    width: 100%;
    padding: 0.875rem 1.125rem;
    border: 2px solid #e5e7eb;
    border-radius: 0.625rem;
    font-size: 0.9375rem;
    font-family: inherit;
    background-color: #ffffff;
    transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
    color: #1f2937;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
    outline: none !important;
    border-color: #5b2e8e;
    box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1), 0 2px 4px 0 rgba(0, 0, 0, 0.05);
    background-color: #fafafa;
}

.form-input:focus-visible,
.form-select:focus-visible,
.form-textarea:focus-visible {
    outline: none !important;
}

.form-input:hover:not(:focus),
.form-select:hover:not(:focus),
.form-textarea:hover:not(:focus) {
    border-color: #d1d5db;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.04);
}

.form-input::placeholder,
.form-textarea::placeholder {
    color: #a0aec0;
}

.form-textarea {
    resize: vertical;
    min-height: 100px;
    line-height: 1.5;
}

.form-input.compact,
.form-select.compact {
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
}

/* File Upload Styles */
.file-input {
    display: none;
}

.upload-area {
    border: 2px dashed #cbd5e0;
    border-radius: 0.625rem;
    padding: 1.25rem 1rem;
    text-align: center;
    cursor: pointer;
    transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    position: relative;
    overflow: hidden;
    min-height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.upload-area::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(91, 46, 142, 0.03), transparent);
    transition: left 0.5s ease;
}

.upload-area:hover {
    border-color: #5b2e8e;
    background: linear-gradient(135deg, #f8f5ff 0%, #f0f4ff 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(91, 46, 142, 0.1);
}

.upload-area:hover::before {
    left: 100%;
}

.upload-area.compact {
    padding: 1rem 0.75rem;
    min-height: 70px;
}

.upload-content {
    pointer-events: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
}

.upload-icon-wrapper {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(91, 46, 142, 0.1) 0%, rgba(124, 77, 189, 0.1) 100%);
    border-radius: 12px;
    margin-bottom: 0.25rem;
    transition: all 0.2s ease;
}

.upload-area:hover .upload-icon-wrapper {
    background: linear-gradient(135deg, rgba(91, 46, 142, 0.15) 0%, rgba(124, 77, 189, 0.15) 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(91, 46, 142, 0.15);
}

.upload-icon-svg {
    width: 24px;
    height: 24px;
    color: #5b2e8e;
    stroke-width: 2;
    transition: all 0.2s ease;
}

.upload-area:hover .upload-icon-svg {
    color: #7c4dbd;
    transform: scale(1.05);
}

.upload-text {
    font-weight: 500;
    color: #5b2e8e;
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.4;
}

.upload-text.small {
    font-size: 0.75rem;
}

.upload-hint {
    font-size: 0.6875rem;
    color: #94a3b8;
    margin: 0;
    font-weight: 400;
    line-height: 1.3;
}

/* Image Preview */
.image-preview {
    margin-top: 1rem;
}

/* Room Images Preview Grid */
.room-images-preview-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 1rem;
    margin-top: 1.5rem;
}

.image-preview-item {
    position: relative;
}

.preview-container {
    position: relative;
    display: inline-block;
    border-radius: 0.5rem;
    overflow: hidden;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.preview-container.uploading {
    pointer-events: none;
}

.preview-container.uploading .preview-image {
    opacity: 0.6;
    filter: blur(2px);
    transition: opacity 0.3s ease, filter 0.3s ease;
}

.preview-image {
    width: 120px;
    height: 120px;
    object-fit: cover;
    border-radius: 0.5rem;
    border: 2px solid #ffffff;
}

/* Object Image Preview - Larger Size */
.object-preview-container {
    display: block;
    width: 100%;
    max-width: 400px;
}

.object-preview-image {
    width: 100%;
    max-width: 400px;
    height: auto;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    border-radius: 0.75rem;
    border: 2px solid #ffffff;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.upload-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    z-index: 10;
    animation: fadeInOverlay 0.3s ease-out;
}

@keyframes fadeInOverlay {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

.spinner-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
}

/* Modern Professional Spinner */
.modern-spinner {
    position: relative;
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.spinner-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border: 3px solid transparent;
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.spinner-ring:nth-child(1) {
    width: 56px;
    height: 56px;
    border-top-color: #ffffff;
    border-right-color: rgba(255, 255, 255, 0.3);
    animation-duration: 1.2s;
    animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
}

.spinner-ring:nth-child(2) {
    width: 44px;
    height: 44px;
    border-top-color: #a78bfa;
    border-right-color: rgba(167, 139, 250, 0.3);
    animation-duration: 1s;
    animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
    animation-direction: reverse;
    top: 6px;
    left: 6px;
}

.spinner-ring:nth-child(3) {
    width: 32px;
    height: 32px;
    border-top-color: #7c3aed;
    border-right-color: rgba(124, 58, 237, 0.3);
    animation-duration: 0.8s;
    animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
    top: 12px;
    left: 12px;
}

@keyframes spin-ring {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

.uploading-text {
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    margin: 0;
    letter-spacing: 0.5px;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    animation: pulse-text 2s ease-in-out infinite;
}

@keyframes pulse-text {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.7;
    }
}

.remove-button {
    position: absolute;
    top: -3px;
    right: -3px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #ef4444;
    color: #ffffff;
    border: 3px solid #ffffff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
    z-index: 10;
}

.remove-button:hover {
    background: #dc2626;
    transform: scale(1.15);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

.remove-icon-svg {
    width: 14px;
    height: 14px;
    stroke-width: 2.5;
}

/* Room Pricing Section */
.room-pricing-section {
    background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
    border-radius: 0.875rem;
    padding: 1.75rem;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

.room-pricing-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.room-pricing-item {
    background: #ffffff;
    border-radius: 0.625rem;
    padding: 1.25rem;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.06);
    transition: all 0.2s ease;
}

.room-pricing-item:hover {
    border-color: #d1d5db;
    box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.08);
}

.pricing-inputs {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 1rem;
    align-items: end;
}

.input-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.input-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    text-align: left;
}

/* Button Styles */
.btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    transition: all 150ms ease-in-out;
    border: 2px solid transparent;
    position: relative;
    overflow: hidden;
}

.btn-primary {
    background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
    color: #ffffff;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.btn-primary:hover:not(:disabled) {
    background: linear-gradient(135deg, #4a2373 0%, #5b2e8e 100%);
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.btn-secondary {
    background: #ffffff;
    color: #2d3748;
    border-color: #cbd5e0;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.btn-secondary:hover {
    background: #f7fafc;
    border-color: #a0aec0;
    transform: translateY(-1px);
}

.btn-add {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: #ffffff;
    border: none;
    padding: 0.875rem 1.25rem;
    border-radius: 0.625rem;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    gap: 0.625rem;
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
}

.btn-add:hover:not(:disabled) {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(16, 185, 129, 0.3);
}

.btn-add .btn-icon-svg {
    width: 18px;
    height: 18px;
    stroke-width: 2.5;
}

.btn-remove {
    background: #ef4444;
    color: #ffffff;
    border: none;
    padding: 0.5rem;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(239, 68, 68, 0.2);
}

.btn-remove:hover:not(:disabled) {
    background: #dc2626;
    transform: scale(1.05);
    box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3);
}

.btn-remove .btn-icon-svg {
    width: 18px;
    height: 18px;
    stroke-width: 2.5;
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
}

.btn-icon {
    font-size: 0.875rem;
    color: inherit;
}

.btn-icon-svg {
    width: 18px;
    height: 18px;
    stroke-width: 2.5;
    flex-shrink: 0;
}

.btn-primary .btn-icon-svg {
    color: #ffffff;
}

.btn-secondary .btn-icon-svg {
    color: #374151;
}

/* Form Actions */
.form-actions {
    position: sticky;
    bottom: 0;
    background: #ffffff;
    padding: 1.5rem 2rem;
    padding-bottom: calc(1.5rem + max(env(safe-area-inset-bottom, 0px), 0px));
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    z-index: 200;
    border-top: 1px solid #e2e8f0;
    margin-top: auto;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
    margin-bottom: 0;
    flex-shrink: 0;
    width: 100%;
    box-sizing: border-box;
}

/* Loading Spinner */
.spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: #ffffff;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* Responsive Design */
@media (max-width: 1024px) {
    .sticky-header {
        position: sticky;
        top: 0;
        z-index: 150;
    }
    
    .form-wrapper {
        padding: 1.5rem;
    }
    
    .header-content {
        padding: 1.5rem 2rem;
    }
    
    .object-form {
        padding: 2rem;
        padding-bottom: 2.5rem;
        margin: 0;
        border-radius: 0;
        max-width: 100%;
    }
    
    .form-section {
        margin-bottom: 2.5rem;
        padding-bottom: 2rem;
    }
    
    .form-grid {
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 1.5rem;
    }
    
    .upload-images-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 1.5rem;
    }
    
    .form-actions {
        padding: 1.25rem 2rem;
        padding-bottom: calc(2rem + max(env(safe-area-inset-bottom, 0px), 30px));
        margin-bottom: 20px;
        position: sticky;
        bottom: 0;
        background: #ffffff;
        box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
        z-index: 200;
        width: 100%;
        box-sizing: border-box;
        display: flex;
        justify-content: flex-end;
        gap: 1rem;
    }
    
    .form-actions .btn {
        flex: 0 0 auto;
        min-width: 120px;
        display: flex;
        align-items: center;
    }
    
    .form-wrapper {
        padding-bottom: 0;
        margin-bottom: 0;
    }
}

@media (max-width: 768px) {
    .sticky-header {
        position: sticky;
        top: 0;
        z-index: 150;
    }
    
    .header-content {
        padding: 1rem;
        flex-direction: column;
        gap: 0.75rem;
        text-align: center;
    }
    
    .page-title {
        font-size: 1.25rem;
    }
    
    .form-wrapper {
        padding: 1rem;
    }
    
    .object-form {
        padding: 1.5rem;
        padding-bottom: 2rem;
        margin: 0;
        border-radius: 0;
        max-width: 100%;
    }
    
    .form-section {
        margin-bottom: 2rem;
        padding-bottom: 1.75rem;
    }
    
    .section-title {
        font-size: 1.25rem;
        margin-bottom: 1.5rem;
        padding-bottom: 0.75rem;
    }
    
    .form-grid {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
    
    .additional-info-grid {
        gap: 1.5rem;
    }
    
    .tag-input-field {
        margin-bottom: 0.75rem;
    }
    
    .description-field {
        margin-top: 0.75rem;
        padding-top: 1.25rem;
    }
    
    .upload-images-grid {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
    
    .pricing-inputs {
        grid-template-columns: 1fr;
        gap: 0.75rem;
    }
    
    .form-actions {
        flex-direction: column;
        gap: 0.75rem;
        padding: 1rem;
        padding-bottom: calc(2rem + max(env(safe-area-inset-bottom, 0px), 30px));
        margin-bottom: 20px;
        margin-top: 1.5rem;
        position: sticky;
        bottom: 0px;
        background: #ffffff;
        box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.12);
        z-index: 200;
        width: 100%;
        box-sizing: border-box;
        min-height: auto;
    }
    
    .form-actions .btn {
        justify-content: center;
        width: 100%;
        padding: 0.875rem 1.5rem;
        min-height: 48px;
        display: flex !important;
        align-items: center;
        flex-shrink: 0;
        opacity: 1;
        visibility: visible;
    }
    
    .form-actions .btn-secondary {
        order: 1;
    }
    
    .form-actions .btn-primary {
        order: 2;
    }
    
    .object-form {
        padding-bottom: 3rem;
        margin-bottom: 0;
    }
    
    .form-wrapper {
        padding-bottom: 0;
        margin-bottom: 0;
    }
}

@media (max-width: 480px) {
    .upload-area {
        padding: 1rem 0.75rem;
        min-height: 70px;
    }
    
    .upload-icon-wrapper {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        margin-bottom: 0.25rem;
    }
    
    .upload-icon-svg {
        width: 20px;
        height: 20px;
    }
    
    .upload-text {
        font-size: 0.75rem;
    }
    
    .upload-hint {
        font-size: 0.625rem;
    }
    
    .section-title {
        font-size: 1.25rem;
    }
    
    .preview-image {
        width: 100px;
        height: 100px;
    }
    
    .object-preview-image {
        max-width: 100%;
        width: 100%;
        aspect-ratio: 4 / 3;
    }
    
    .form-actions {
        flex-direction: column;
        gap: 0.75rem;
        padding: 1rem;
        padding-bottom: calc(2rem + max(env(safe-area-inset-bottom, 0px), 30px));
        margin-bottom: 20px;
        margin-top: 1.5rem;
        position: sticky;
        bottom: 0;
        background: #ffffff;
        box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.12);
        z-index: 200;
        width: 100%;
        box-sizing: border-box;
        min-height: auto;
    }
    
    .form-actions .btn {
        justify-content: center;
        width: 100%;
        padding: 0.875rem 1.5rem;
        min-height: 48px;
        display: flex !important;
        align-items: center;
        flex-shrink: 0;
        opacity: 1;
        visibility: visible;
    }
    
    .form-actions .btn-secondary {
        order: 1;
    }
    
    .form-actions .btn-primary {
        order: 2;
    }
    
    .object-form {
        padding-bottom: 3rem;
        margin-bottom: 0;
    }
    
    .form-wrapper {
        padding-bottom: 0;
        margin-bottom: 0;
    }
}

/* Focus visible for accessibility */
.btn:focus-visible {
    outline: 2px solid #5b2e8e;
    outline-offset: 2px;
}

.form-input:focus-visible,
.form-select:focus-visible,
.form-textarea:focus-visible {
    outline: none !important;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
    .form-input,
    .form-select,
    .form-textarea {
        border-color: #666;
    }
    
    .field-help {
        color: #333;
    }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
    * {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
    }
}

/* Print styles */
@media print {
    .sticky-header,
    .form-actions {
        display: none;
    }
    
    .main-container {
        background: white;
    }
    
    .object-form {
        box-shadow: none;
        border: 1px solid #ccc;
    }
}

/* Toast Notification Styles */
.toast-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 10001;
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 400px;
    pointer-events: none;
}

.toast {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05);
    border-left: 4px solid;
    pointer-events: auto;
    min-width: 300px;
}

.toast-error {
    border-left-color: #ef4444;
    background: linear-gradient(135deg, #fef2f2, #ffffff);
}

.toast-success {
    border-left-color: #10b981;
    background: linear-gradient(135deg, #f0fdf4, #ffffff);
}

.toast-info {
    border-left-color: #3b82f6;
    background: linear-gradient(135deg, #eff6ff, #ffffff);
}

.toast-warning {
    border-left-color: #f59e0b;
    background: linear-gradient(135deg, #fffbeb, #ffffff);
}

.toast-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
}

.toast-error .toast-icon {
    color: #ef4444;
}

.toast-success .toast-icon {
    color: #10b981;
}

.toast-info .toast-icon {
    color: #3b82f6;
}

.toast-warning .toast-icon {
    color: #f59e0b;
}

.toast-content {
    flex: 1;
    min-width: 0;
}

.toast-message {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    line-height: 1.4;
}

.toast-close {
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s;
    flex-shrink: 0;
}

.toast-close:hover {
    color: #6b7280;
    background: rgba(0, 0, 0, 0.05);
    transform: scale(1.1);
}

/* Toast Animations */
.toast-enter-active {
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
}

.toast-enter-from {
    opacity: 0;
    transform: translateX(100%) scale(0.95);
}

.toast-leave-to {
    opacity: 0;
    transform: translateX(100%) scale(0.95);
}

.toast-move {
    transition: transform 0.3s ease;
}

/* Responsive Toast Styles */
@media (max-width: 768px) {
    .toast-container {
        top: 16px;
        right: 16px;
        left: 16px;
        max-width: none;
    }

    .toast {
        min-width: auto;
        width: 100%;
        padding: 14px 16px;
    }

    .toast-message {
        font-size: 14px;
    }
}
</style>